import { createAsyncThunk } from '@reduxjs/toolkit';
import AdminAPI from './adminApi';

// Health check
export const fetchAdminHealthThunk = createAsyncThunk(
  'admin/fetchHealth',
  async (_, { rejectWithValue }) => {
    try {
      return await AdminAPI.health();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Fetch user details
export const fetchAdminUserThunk = createAsyncThunk(
  'admin/fetchUser',
  async (userId, { rejectWithValue }) => {
    try {
      return await AdminAPI.fetchUser(userId);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Bulk user action
export const bulkAdminUserActionThunk = createAsyncThunk(
  'admin/bulkUserAction',
  async (actionRequest, { rejectWithValue }) => {
    try {
      return await AdminAPI.bulkUserAction(actionRequest);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Permanently erase a user's account (GDPR Article 17) -- separate from
// bulkUserAction since it's a single, irreversible target, not a batchable
// reversible flag flip.
export const eraseAdminUserThunk = createAsyncThunk(
  'admin/eraseUser',
  async ({ userId, reason, confirm_username }, { rejectWithValue }) => {
    try {
      const result = await AdminAPI.eraseUser(userId, { reason, confirm_username });
      return { userId, ...result };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Data export
export const exportAdminDataThunk = createAsyncThunk(
  'admin/exportData',
  async (exportRequest, { rejectWithValue }) => {
    try {
      return await AdminAPI.exportData(exportRequest);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// System stats
export const fetchAdminSystemStatsThunk = createAsyncThunk(
  'admin/fetchSystemStats',
  async (_, { rejectWithValue }) => {
    try {
      return await AdminAPI.fetchSystemStats();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// Fetch all users with pagination
export const fetchAdminUsersListThunk = createAsyncThunk(
  'admin/fetchUsersList',
  async (params = { page: 1, limit: 50 }, { rejectWithValue }) => {
    try {
      return await AdminAPI.fetchUsersList(params);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);


/** Fetch importable models */
export const fetchImportModelsThunk = createAsyncThunk(
  "admin/fetchImportModels",
  async (_, { rejectWithValue }) => {
    try {
      const data = await AdminAPI.fetchImportModels();
      return data.models;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch models");
    }
  }
);

/** Validate CSV */
export const validateCSVThunk = createAsyncThunk(
  'admin/validateCSV',
  async ({ file, target_models, field_mapping }, { rejectWithValue }) => {
    try {
      return await AdminAPI.validateCSV({ file, target_models, field_mapping });
    } catch (error) {
      return rejectWithValue(error.message || "CSV validation failed");
    }
  }
);


/** Import CSV */
export const importCSVThunk = createAsyncThunk(
  'admin/importCSV',
  async ({ file, target_models, field_mapping }, { rejectWithValue }) => {
    try {
      return await AdminAPI.importCSV({ file, target_models, field_mapping });
    } catch (error) {
      return rejectWithValue(error.message || "CSV import failed");
    }
  }
);

export const fetchAdminAuditLogsThunk = createAsyncThunk(
  'admin/fetchAuditLogs',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await AdminAPI.fetchAuditLogs(params);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail ||
        error.message ||
        'Failed to fetch audit logs'
      );
    }
  }
);